import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { useDispatch, useSelector } from 'react-redux';
import {
    setCommandMessage,
    resetOperation,
    setOperationData,
    setOperationStep,
} from '../../../redux/operationSlice';
import { generateUniqueId } from '../../../utils/generateUniqueId';
import { COMMANDS } from '../../../utils/commonProperties';
import { createDrawingClickHandler, applyGridSnap, getOrthoSnappedPoint } from "./useDrawingEvents";
import { getSnappedPoint } from '../../../shared/snap/getSnappedPoint';
import { createSnapMarker } from '../../../shared/snap/createSnapMarker';
import { createEscapeHandler } from '../../../utils/escapeHandler';
import { DrawCommand } from '../../../modules/undoRedo';
import { addUserEntity } from '../../../redux/dxfSlice';

const useDrawRect = (scene, camera, renderer, snapPoints) => {
    const snapMarkerRef = useRef(null);
    const tempRectRef = useRef(null);
    const orthoEndPointRef = useRef(null);
    const dispatch = useDispatch();

    const { commandType, step, data, drawMethod } = useSelector(state => state.operation);
    const gridMode = useSelector(state => state.mods.gridMode);
    const objectSnap = useSelector(state => state.mods.objectSnap);
    const orthoMode = useSelector(state => state.mods.orthoMode);
    const snapMode = useSelector((state) => state.mods.snapMode);
    const { activeLayerName } = useSelector((state) => state.layer);
    const activeTabId = useSelector(state => state.projectTabs.activeTabId);
    const serializeVector = (v) => ({ x: v.x, y: v.y, z: v.z });

    const getOrthoPointForRect = (start, end, rawPoint) => {
        const baseVec = end.clone().sub(start);
        const baseAngle = Math.atan2(baseVec.y, baseVec.x);
        const dir = new THREE.Vector2().subVectors(
            new THREE.Vector2(rawPoint.x, rawPoint.y),
            new THREE.Vector2(start.x, start.y)
        );
        const projectionLength = Math.abs(dir.x * Math.sin(baseAngle) - dir.y * Math.cos(baseAngle));
        const heightVector = new THREE.Vector3(
            -Math.sin(baseAngle) * projectionLength,
            Math.cos(baseAngle) * projectionLength,
            0
        );
        return start.clone().add(heightVector);
    };

    const drawRectangle = (start, end, third, color = 0xffff00, isPreview = true) => {
        const baseVec = end.clone().sub(start);
        const baseLength = baseVec.length();
        const baseAngle = Math.atan2(baseVec.y, baseVec.x);
        const heightVec = third.clone().sub(start).applyAxisAngle(new THREE.Vector3(0, 0, 1), -baseAngle);
        const height = heightVec.y;

        const rectPoints = [
            new THREE.Vector3(0, 0, 0),
            new THREE.Vector3(baseLength, 0, 0),
            new THREE.Vector3(baseLength, height, 0),
            new THREE.Vector3(0, height, 0),
        ];

        const rotationMatrix = new THREE.Matrix4()
            .makeRotationZ(baseAngle)
            .setPosition(start.x, start.y, 0);

        const finalPoints = rectPoints.map(p => p.clone().applyMatrix4(rotationMatrix));
        const geometry = new THREE.BufferGeometry().setFromPoints(finalPoints);
        const material = new THREE.LineBasicMaterial({ color });

        const rect = new THREE.LineLoop(geometry, material);
        rect.userData = {
            id: generateUniqueId(),
            isSelectable: true,
            type: "rect",
            start: { x: start.x, y: start.y, z: 0 },
            width: baseLength,
            height: height,
            rotation: baseAngle,
            points: finalPoints.map(p => ({ x: p.x, y: p.y, z: p.z })),
        };

        if (isPreview) {
            if (tempRectRef.current) {
                scene.remove(tempRectRef.current);
                tempRectRef.current.geometry.dispose();
                tempRectRef.current.material.dispose();
            }
            tempRectRef.current = rect;
        }

        scene.add(rect);
        return rect;
    };

    const drawCenterCornerRect = (center, corner, color = 0xffff00, isPreview = true) => {
        const halfW = Math.abs(corner.x - center.x);
        const halfH = Math.abs(corner.y - center.y);

        const finalPoints = [
            new THREE.Vector3(center.x - halfW, center.y - halfH, 0),
            new THREE.Vector3(center.x + halfW, center.y - halfH, 0),
            new THREE.Vector3(center.x + halfW, center.y + halfH, 0),
            new THREE.Vector3(center.x - halfW, center.y + halfH, 0),
        ];

        const geometry = new THREE.BufferGeometry().setFromPoints(finalPoints);
        const material = new THREE.LineBasicMaterial({ color });
        const rect = new THREE.LineLoop(geometry, material);
        rect.userData = {
            id: generateUniqueId(),
            isSelectable: true,
            type: "rect",
            start: { x: center.x - halfW, y: center.y - halfH, z: 0 },
            width: halfW * 2,
            height: halfH * 2,
            rotation: 0,
            points: finalPoints.map(p => ({ x: p.x, y: p.y, z: p.z })),
        };

        if (isPreview) {
            if (tempRectRef.current) {
                scene.remove(tempRectRef.current);
                tempRectRef.current.geometry.dispose();
                tempRectRef.current.material.dispose();
            }
            tempRectRef.current = rect;
        }

        scene.add(rect);
        return rect;
    };

    useEffect(() => {
        if (commandType !== COMMANDS.DRAW_RECT || !scene || !camera || !renderer) return;

        const domElement = renderer.domElement;
        const method = drawMethod || 'diagonal_corners';

        // Initial message
        if (method === 'diagonal_corners') {
            dispatch(setCommandMessage("İlk köşe noktasını seçin"));
        } else if (method === '2corner') {
            dispatch(setCommandMessage("İlk köşe noktasını seçin"));
        } else if (method === 'center_corner') {
            dispatch(setCommandMessage("Merkez noktasını seçin"));
        }

        const handleRectPoint = (point) => {
            if (method === 'diagonal_corners') {
                if (step === 0) {
                    dispatch(setOperationData({ corner1: serializeVector(point) }));
                    dispatch(setOperationStep(1));
                    dispatch(setCommandMessage("Karşı köşe noktasını seçin"));
                } else if (step === 1) {
                    const c1 = new THREE.Vector3(data.corner1.x, data.corner1.y, data.corner1.z);
                    const c3 = point.clone();
                    const minX = Math.min(c1.x, c3.x), maxX = Math.max(c1.x, c3.x);
                    const minY = Math.min(c1.y, c3.y), maxY = Math.max(c1.y, c3.y);
                    const start = new THREE.Vector3(minX, minY, 0);
                    const corner = new THREE.Vector3(maxX, maxY, 0);
                    const center = start.clone().add(corner).multiplyScalar(0.5);
                    const finalRect = drawCenterCornerRect(center, corner, 0xffffff, false);

                    // Redux'a DXF entity olarak kaydet (JBZ'ye dahil edilecek)
                    if (finalRect) {
                        dispatch(addUserEntity({
                            type: 'LWPOLYLINE',
                            vertices: finalRect.userData.points.map(p => ({ x: p.x, y: p.y, z: 0 })),
                            shape: true,
                            layer: activeLayerName || '0',
                            _userEntityId: finalRect.userData.id,
                            _userDrawn: true,
                            _tabId: activeTabId || 'mimari',
                        }));
                    }

                    // Undo-redo kaydı
                    const mgr1 = window.undoRedoManager;
                    if (mgr1 && finalRect) {
                        const tx = mgr1.beginTransaction('Draw Rect');
                        mgr1.addCommand(new DrawCommand(finalRect));
                        mgr1.commitTransaction(tx);
                    }
                    dispatch(resetOperation());
                    dispatch(setCommandMessage(""));
                }
            } else if (method === '2corner') {
                if (step === 0) {
                    dispatch(setOperationData({ start: serializeVector(point) }));
                    dispatch(setOperationStep(1));
                    dispatch(setCommandMessage("Taban kenar için ikinci köşeyi seçin"));
                } else if (step === 1) {
                    const usedPoint = orthoMode && orthoEndPointRef.current
                        ? orthoEndPointRef.current.clone()
                        : point.clone();
                    dispatch(setOperationData({ ...data, end: serializeVector(usedPoint) }));
                    dispatch(setOperationStep(2));
                    dispatch(setCommandMessage("Dik kenar yüksekliği için üçüncü noktayı seçin"));
                } else if (step === 2) {
                    const start = new THREE.Vector3(data.start.x, data.start.y, data.start.z);
                    const end = new THREE.Vector3(data.end.x, data.end.y, data.end.z);
                    const rawThird = point.clone();
                    const third = orthoMode ? getOrthoPointForRect(start, end, rawThird) : rawThird;
                    const finalRect2 = drawRectangle(start, end, third, 0xffffff, false);

                    // Redux'a DXF entity olarak kaydet (JBZ'ye dahil edilecek)
                    if (finalRect2) {
                        dispatch(addUserEntity({
                            type: 'LWPOLYLINE',
                            vertices: finalRect2.userData.points.map(p => ({ x: p.x, y: p.y, z: 0 })),
                            shape: true,
                            layer: activeLayerName || '0',
                            _userEntityId: finalRect2.userData.id,
                            _userDrawn: true,
                            _tabId: activeTabId || 'mimari',
                        }));
                    }

                    // Undo-redo kaydı
                    const mgr2 = window.undoRedoManager;
                    if (mgr2 && finalRect2) {
                        const tx = mgr2.beginTransaction('Draw Rect');
                        mgr2.addCommand(new DrawCommand(finalRect2));
                        mgr2.commitTransaction(tx);
                    }
                    dispatch(resetOperation());
                    dispatch(setCommandMessage(""));
                }
            } else if (method === 'center_corner') {
                if (step === 0) {
                    dispatch(setOperationData({ center: serializeVector(point) }));
                    dispatch(setOperationStep(1));
                    dispatch(setCommandMessage("Köşe noktasını seçin"));
                } else if (step === 1) {
                    const center = new THREE.Vector3(data.center.x, data.center.y, data.center.z);
                    const finalRect3 = drawCenterCornerRect(center, point, 0xffffff, false);

                    // Redux'a DXF entity olarak kaydet (JBZ'ye dahil edilecek)
                    if (finalRect3) {
                        dispatch(addUserEntity({
                            type: 'LWPOLYLINE',
                            vertices: finalRect3.userData.points.map(p => ({ x: p.x, y: p.y, z: 0 })),
                            shape: true,
                            layer: activeLayerName || '0',
                            _userEntityId: finalRect3.userData.id,
                            _userDrawn: true,
                            _tabId: activeTabId || 'mimari',
                        }));
                    }

                    // Undo-redo kaydı
                    const mgr3 = window.undoRedoManager;
                    if (mgr3 && finalRect3) {
                        const tx = mgr3.beginTransaction('Draw Rect');
                        mgr3.addCommand(new DrawCommand(finalRect3));
                        mgr3.commitTransaction(tx);
                    }
                    dispatch(resetOperation());
                    dispatch(setCommandMessage(""));
                }
            }
        };

        const handleClick = createDrawingClickHandler({
            scene,
            camera,
            renderer,
            snapPoints,
            snapMarkerRef,
            gridMode,
            objectSnap,
            snapMode,
            onPointSelected: handleRectPoint
        });

        const handleMouseMove = (e) => {
            const rect = domElement.getBoundingClientRect();
            const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
            const y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
            const mouseNDC = new THREE.Vector2(x, y);
            const worldPoint = new THREE.Vector3(x, y, 0).unproject(camera);

            const { finalPoint, snapped, snapSource } = getSnappedPoint(
                mouseNDC,
                worldPoint,
                snapPoints,
                camera,
                renderer,
                { snapMode }
            );
            const point = (objectSnap && snapped) ? finalPoint.clone() : (gridMode ? applyGridSnap(worldPoint) : worldPoint.clone());

            // Snap marker
            if (snapMarkerRef.current) {
                scene.remove(snapMarkerRef.current);
                snapMarkerRef.current.geometry.dispose();
                snapMarkerRef.current.material.dispose();
                snapMarkerRef.current = null;
            }

            if (snapped && snapSource) {
                const marker = createSnapMarker(snapSource);
                marker.position.copy(finalPoint);
                scene.add(marker);
                snapMarkerRef.current = marker;
            }

            // Preview
            if (method === 'diagonal_corners') {
                if (step === 1 && data.corner1) {
                    const c1 = new THREE.Vector3(data.corner1.x, data.corner1.y, data.corner1.z);
                    const c3 = point.clone();
                    const minX = Math.min(c1.x, c3.x), maxX = Math.max(c1.x, c3.x);
                    const minY = Math.min(c1.y, c3.y), maxY = Math.max(c1.y, c3.y);
                    const start = new THREE.Vector3(minX, minY, 0);
                    const corner = new THREE.Vector3(maxX, maxY, 0);
                    const center = start.clone().add(corner).multiplyScalar(0.5);
                    drawCenterCornerRect(center, corner, 0xffff00, true);
                }
            } else if (method === '2corner') {
                if (step === 1) {
                    const start = new THREE.Vector3(data.start.x, data.start.y, data.start.z);
                    const preview = orthoMode ? getOrthoSnappedPoint(start, point) : point;
                    orthoEndPointRef.current = preview.clone();
                    drawRectangle(start, preview, preview, 0xffff00, true);
                } else if (step === 2) {
                    const start = new THREE.Vector3(data.start.x, data.start.y, data.start.z);
                    const end = new THREE.Vector3(data.end.x, data.end.y, data.end.z);
                    const third = orthoMode ? getOrthoPointForRect(start, end, point) : point;
                    drawRectangle(start, end, third, 0xffff00, true);
                }
            } else if (method === 'center_corner') {
                if (step === 1 && data.center) {
                    const center = new THREE.Vector3(data.center.x, data.center.y, data.center.z);
                    drawCenterCornerRect(center, point, 0xffff00, true);
                }
            }
        };

        const handleKeyDown = createEscapeHandler({
            scene,
            dispatch,
            tempObjectRef: tempRectRef,
            snapMarkerRef,
            additionalCleanup: () => {
                dispatch(setCommandMessage(""));
            }
        });

        domElement.addEventListener('click', handleClick);
        domElement.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('keydown', handleKeyDown);

        return () => {
            domElement.removeEventListener('click', handleClick);
            domElement.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [commandType, step, data, scene, camera, orthoMode, renderer, dispatch, gridMode, snapMode, objectSnap, drawMethod]);
};

export default useDrawRect;
